import React from "react";
import { difference, intersection, union } from "lodash";
import moment from "moment";
import { TaskData } from "core/queries/GetTasks";
import { SearchForTaskContextsItem } from "core/queries/SearchForTaskContexts";
import { ExternalLink } from "tasks/components/TaskPanel/ExternalLink";
import { ENABLED_TASK_TYPE_OPTIONS, TASK_TYPE_OPTIONS } from "tasks/constants";
import { SlicedSerializedTaskContext } from "tasks/hooks/useCreateTaskFormConfig";
import {
  DispatchRoleName,
  TaskFilterInput,
  TypeEnum as TaskTypeEnum,
} from "typings/globalGQLTypes";

export const canViewGuestTab = (user: User) => {
  return user.isGETMember || user.isTOMMember;
};

export const canViewBackToTasks = (user: User | null, pathname: string) => {
  if (pathname.includes("/task/"))
    return [DispatchRoleName.tom_team_lead, DispatchRoleName.tom_manager].some(
      role => user?.roles && user?.roles.includes(role),
    );

  return false;
};

export const normalizeType = (rawType: string | TaskTypeEnum) =>
  rawType.replace(/::/g, "_");

export const isCleanTask = (taskType: string) => {
  return normalizeType(taskType) === TaskTypeEnum.Tom_Tasks_Clean;
};

export const isSpaceCleanTask = (taskType: string) => {
  return normalizeType(taskType) === TaskTypeEnum.Tom_Tasks_SpaceClean;
};

export const getTaskOptionByType = (
  rawType: string | TaskTypeEnum,
  filterDisabled: boolean = true,
) => {
  return (
    (filterDisabled ? ENABLED_TASK_TYPE_OPTIONS : TASK_TYPE_OPTIONS).find(
      ({ taskType }) => taskType === normalizeType(rawType),
    ) || null
  );
};

export interface FilterPair {
  globalFilters: TaskFilterInput;
  localFilters: TaskFilterInput;
}

export const parseGraphQLId = (id: string) => id.split("|").pop();
export const formatDate = (value: string) =>
  moment(value).format("MMM D, h:mm A");

export const getCommonDates = () => ({
  startOfToday: moment().startOf("day").toISOString(),
  endOfToday: moment().startOf("day").add(1, "day").toISOString(),
  endOfTomorrow: moment().startOf("day").add(2, "day").toISOString(),
});

export const formatAccountName = (account: any) => {
  return account && (account.firstName || account.lastName)
    ? [
        account.firstName,
        account.lastName ? `${account.lastName.slice(0, 1)}.` : false,
      ]
        .filter(n => !!n)
        .join(" ")
    : "(Unknown)";
};

const isSameOrBeforeSafe = (date: string | null, compareTo: string | null) => {
  if (date === null && compareTo === null) return true;
  if (date === null) return true;
  if (compareTo === null) return false;
  return moment(date).isSameOrBefore(compareTo);
};

export const isPartlyWithinDateRange = ({
  globalFilters,
  localFilters,
}: FilterPair) =>
  isSameOrBeforeSafe(localFilters.dueAtStart, globalFilters.dueAtEnd) ||
  isSameOrBeforeSafe(globalFilters.dueAtStart, localFilters.dueAtEnd);

export const areDueAtFiltersActive = ({ globalFilters }: FilterPair) =>
  !!globalFilters.dueAtEnd || !!globalFilters.dueAtStart;

// When both global and local priority filters are set, keep only the
// priorities that appear in both sets.
// Ex. (global=[1,2,3], local=[3,4]) => [3]
export const intersectPriorityFilters = ({
  globalFilters,
  localFilters,
}: FilterPair): TaskFilterInput => ({
  priority:
    globalFilters.priority && localFilters.priority
      ? intersection(globalFilters.priority, localFilters.priority).sort()
      : globalFilters.priority || localFilters.priority || null,
});

// Add all local priority filters to global filters
// Ex. (global=[1,2,3], local=[3,4]) => [1,2,3,4]
export const unionPriorityFilters = ({
  globalFilters,
  localFilters,
}: FilterPair): TaskFilterInput => ({
  priority:
    globalFilters.priority && localFilters.priority
      ? union(globalFilters.priority, localFilters.priority).sort()
      : globalFilters.priority || localFilters.priority || null,
});

// Unset all local priority filters from global filters
// Ex. (global=[1,2,3], local=[3,4]) => [1,2]
export const diffPriorityFilters = ({
  globalFilters,
  localFilters,
}: FilterPair): TaskFilterInput => ({
  priority:
    globalFilters.priority && localFilters.priority
      ? difference(globalFilters.priority, localFilters.priority).sort()
      : globalFilters.priority || null,
});

// Add all local status filters to global filters
export const unionStatusFilters = ({
  globalFilters,
  localFilters,
}: FilterPair): TaskFilterInput => ({
  status:
    globalFilters.status && localFilters.status
      ? union(globalFilters.status, localFilters.status).sort()
      : globalFilters.status || localFilters.status || null,
});

// Unset all local status filters from global filters
export const diffStatusFilters = ({
  globalFilters,
  localFilters,
}: FilterPair): TaskFilterInput => ({
  status:
    globalFilters.status && localFilters.status
      ? difference(globalFilters.status, localFilters.status).sort()
      : globalFilters.status || null,
});

// Add all local tomTag filters to global filters
// Ex. (global=[{ id: "Tom::Tag|1" }], local=[{ id: "Tom::Tag|2" }]) => [{ id: "Tom::Tag|1" },{ id: "Tom::Tag|2" }]
export const unionTomTagFilters = ({
  globalFilters,
  localFilters,
}: FilterPair): TaskFilterInput => ({
  tomTags:
    globalFilters.tomTags && localFilters.tomTags
      ? union(globalFilters.tomTags, localFilters.tomTags).sort()
      : globalFilters.tomTags || localFilters.tomTags || null,
});

// Unset all local tomTag filters from global filters
export const diffTomTagFilters = ({
  globalFilters,
  localFilters,
}: FilterPair): TaskFilterInput => ({
  tomTags:
    globalFilters.tomTags && localFilters.tomTags
      ? difference(globalFilters.tomTags, localFilters.tomTags).sort()
      : globalFilters.tomTags || null,
});
export const convertToMoment = (date: string | null) =>
  date ? moment(date) : null;
export const truncateString = (str: string, num: number) =>
  str.length <= num ? str : `${str.slice(0, num)}...`;

export const isDuringStayFilterActive = ({
  globalFilters,
  localFilters,
}: FilterPair) => globalFilters.duringStay === localFilters.duringStay;

export const arePriorityFiltersActive = ({
  globalFilters,
  localFilters,
}: FilterPair) =>
  !!localFilters.priority &&
  localFilters.priority.every(p =>
    globalFilters.priority ? globalFilters.priority.includes(p) : false,
  );

export const areStatusFiltersActive = ({
  globalFilters,
  localFilters,
}: FilterPair) => {
  return (
    !!localFilters.status &&
    localFilters.status.every(p =>
      globalFilters.status ? globalFilters.status.includes(p) : false,
    )
  );
};

export type MetadataLinkProps = {
  eventName?: string;
  href: string;
  segmentElement: string;
  task: Pick<TaskData, "id">;
};

export const MetadataLink = ({
  eventName = "task_panel",
  segmentElement,
  href,
  task,
}: MetadataLinkProps) => (
  <ExternalLink
    href={href}
    segmentOnClickEvent={{
      eventName,
      element: segmentElement,
      action: "click",
      props: { taskId: task.id },
    }}
  />
);

export const convertTaskContextToNode = (
  context: SearchForTaskContextsItem,
): SlicedSerializedTaskContext | null => {
  const { resource } = context;

  if (resource.__typename !== "TaskContext") return null;

  const contextId = parseGraphQLId(resource.id) || "";
  const contextName = resource.name || "";
  const contextFeatures = (resource.contextFeatures || []).map(feature => ({
    ...feature,
    name: feature.name || "",
    defaultValue: feature.defaultValue || "",
  }));
  const contextItemName = (resource.contextDetails || []).reduce(
    (acc, memo) => (memo.contextItem ? `${acc} ${memo.contextItem.name}` : acc),
    "",
  );
  const contextItemDisplayName = (resource.contextDetails || []).reduce(
    (acc, memo) =>
      memo.contextItem ? `${acc} ${memo.contextItem.displayName}` : acc,
    "",
  );
  const contextItemNameLocalized = (resource.contextDetails || []).reduce(
    (acc, memo) =>
      memo.contextItem ? `${acc} ${memo.contextItem.nameLocalized}` : acc,
    "",
  );
  const contextItemFeatures = (resource.contextFeatures || []).map(feature => ({
    ...feature,
    name: feature.name || "",
    defaultValue: feature.defaultValue || "",
  }));
  return {
    contextId,
    contextFeatures,
    contextName,
    contextItemName,
    contextItemDisplayName,
    contextItemFeatures,
    contextItemNameLocalized,
  };
};
